import React, { useContext, useEffect, useRef, useState } from 'react';
import { ContentTrackerProps } from './types';
import { FullScreenTrackingContext } from './FullScreenTrackingContext';
import { WindowFocusContext } from './WindowFocusContext';
import { TrackEvent } from '../../../enums/Content/TrackEvent';
import EmbeddableViewer, {
  EmbeddableViewerProps,
} from '../../Embeddable/EmbeddableViewer';
import SimpleTracker from '../../../Services/Analytics/SimpleTracker';
import TrackInterface from '../../../graphql/Rooms/TrackInterface';
import useIntersectionObserver, {
  VISIBILITY_THRESHOLD,
} from '../../../lib/hook/useIntersectionObserver';

export interface EmbeddableTrackerProps
  extends ContentTrackerProps,
    EmbeddableViewerProps {
  onLoadWithinViewport?: () => void;
  onView?: () => void;
}

const EmbeddableTracker: React.FC<EmbeddableTrackerProps> = (
  props: EmbeddableTrackerProps
): JSX.Element => {
  const { fullScreenContentId } = useContext(FullScreenTrackingContext);
  const { isWindowFocused } = useContext(WindowFocusContext);

  const {
    contactId,
    contentId,
    onView,
    roomId,
    track,
    version,
    ...embeddableViewerProps
  } = props;

  const [withinViewport, setWithinViewport] = useState(false);

  /**
   * Initializing the tracker, ignoring to pause the tracker if the window loses
   * focus, as this will happen as soon as the user interacts with the iframe.
   */
  const { current: tracker } = useRef(new SimpleTracker());

  const hasBeenViewed = useRef(false);

  const ref = useIntersectionObserver<HTMLDivElement>(
    (isIntersecting: boolean) => setWithinViewport(isIntersecting),
    VISIBILITY_THRESHOLD
  );

  useEffect(() => {
    if (track) {
      tracker.attach(trackEvent);
    }

    return () => tracker.detach();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    /**
     * Activating tracking when these rules are met:
     *  1. The content is within viewport
     *  2. The window/tab must be focused in order to prevent tracking while the tab
     *     is loading in the background or the user jumps to another window
     *  3. There is no content currently being displayed in full screen
     *  4. OR the current content that's in full screen is this content
     */
    const isAnyContentInFullScreen = !!fullScreenContentId;
    const isCurrentlyInFullScreen = fullScreenContentId === contentId;

    const isTrackerActive =
      !!track &&
      withinViewport &&
      isWindowFocused &&
      (!isAnyContentInFullScreen || isCurrentlyInFullScreen);

    tracker.setActive(isTrackerActive);

    // embeddable contents are "viewed" once, as soon as they enter the viewport
    if (isTrackerActive && !hasBeenViewed.current && onView) {
      hasBeenViewed.current = true;
      onView();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullScreenContentId, isWindowFocused, withinViewport]);

  const trackEvent = (measure: number) => {
    if (!(track && roomId && version)) {
      return;
    }

    try {
      new TrackInterface(contactId).track({
        contentId,
        event: TrackEvent.EMBEDDABLE_VIEWED_SECONDS,
        measure,
        metadata: undefined,
        roomId,
        version,
      });
    } catch (e) {
      // user shouldn't be alerted about this fail, but we're catching
      // this exception to avoid "Uncaught promise reject" warnings
    }
  };

  return <EmbeddableViewer ref={ref} {...embeddableViewerProps} />;
};

export default EmbeddableTracker;
